import { DEFAULT_NON_AWL_SUPPORT_CENTER_URL, nonTranslatableText } from '@va/constants';
import { setCustomizationOptions, setLocale, setWindowWidth } from '@va/dashboard/actions/ui';
import { ChatwootWidget, NotificationV2 } from '@va/dashboard/components';
import { DashboardGlobalFiltersConfigProvider, DashboardReportBlockConfigProvider } from '@va/dashboard/shared/filters';
import { useDefaultTranslationOptions } from '@va/dashboard/util-hooks';
import { Config as BaseConfig } from '@va/http-client';
import { getTranslationsUrl, TranslationsProvider, useLocalizationContext } from '@va/localization';
import { RemoveAdblockerPage } from '@va/shared/remove-adblocker-page';
import { appHistory } from '@va/shared/router';
import { ConfirmationDialogContextProvider, RouterWarningProvider } from '@va/shared/util-confirmation';
import { T3_URL_PARAMS } from '@va/standalone/shared/constants';
import { createStore } from '@va/store';
import { LoaderBar } from '@va/ui/design-system';
import { ErrorBoundary } from '@va/util/components';
import { debounce, extractLanguage, updateUrlParameter } from '@va/util/helpers';
import Config from 'Config';
import flagsmith from 'flagsmith';
import { FlagsmithProvider } from 'flagsmith/react';
import moment from 'moment';
import { PropsWithChildren, StrictMode } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { SWRConfig } from 'swr';
import * as Actions from './actions';
import rootReducer from './reducers';
import { history } from './router/history';
import Routes from './router/routes';
import rootSaga from './sagas';

const locale = extractLanguage();

moment.locale(locale);

const getAuthenticateHeaders = (store: any) => () => {
  const state = store.getState();
  const auth = state.getIn(['core', 'authentication']).toJS();
  const headers: Record<string, unknown> = {};

  if (auth.token) {
    headers['Authorization'] = 'Bearer ' + auth.token;
  }

  return headers;
};

const store = createStore(rootReducer, rootSaga);

window.addEventListener(
  'resize',
  debounce(() => {
    store.dispatch(setWindowWidth(window.innerWidth));
  }, 150),
);
BaseConfig.setAuthProvider(getAuthenticateHeaders(store) as any);
BaseConfig.setApiBaseUrl(Config.apiUrl);
store.dispatch(Actions.initialize());
store.dispatch(setWindowWidth(window.innerWidth));
store.dispatch(
  setCustomizationOptions({
    providerName: nonTranslatableText.appName,
    supportCenterDomain: DEFAULT_NON_AWL_SUPPORT_CENTER_URL,
    chatwootToken: Config.chatwootToken,
  }),
);

const fetchTranslationsFn = async (locale = 'en') => {
  const response = await fetch(getTranslationsUrl(locale));

  const responseText = await response.text();

  return JSON.parse(responseText.replace(/\\n/g, '<br/>'));
};

const WebsiteViewerApp = () => {
  return (
    <Providers>
      <App />
    </Providers>
  );
};

export default WebsiteViewerApp;

const Providers = ({ children }: PropsWithChildren) => {
  return (
    <StrictMode>
      <FlagsmithProvider flagsmith={flagsmith}>
        <SWRConfig value={{ revalidateIfStale: false, revalidateOnFocus: false }}>
          <SkeletonTheme baseColor='#F9F9F9' highlightColor='#EEEEEE'>
            <Provider store={store}>
              <TranslationsProvider
                onLocaleChange={(locale) => {
                  store.dispatch(setLocale(locale));
                }}
                fetchTranslations={fetchTranslationsFn}
                updateURL={(newLocale) => {
                  const updatedURL = updateUrlParameter(T3_URL_PARAMS.locale, newLocale);
                  appHistory.replace(updatedURL);
                }}
                useDefaultOptions={useDefaultTranslationOptions}
              >
                <DashboardReportBlockConfigProvider>
                  <ConfirmationDialogContextProvider>
                    <DashboardGlobalFiltersConfigProvider>{children}</DashboardGlobalFiltersConfigProvider>
                  </ConfirmationDialogContextProvider>
                </DashboardReportBlockConfigProvider>
              </TranslationsProvider>
            </Provider>
          </SkeletonTheme>
        </SWRConfig>
      </FlagsmithProvider>
    </StrictMode>
  );
};

const App = () => {
  const { isLoading: translationsLoading, translationsError } = useLocalizationContext();
  if (translationsLoading) return <LoaderBar />;
  if (translationsError) return <RemoveAdblockerPage />;

  return (
    <Router history={history}>
      <ErrorBoundary>
        <ChatwootWidget forcedLocale='en' />
        <NotificationV2 />
        <RouterWarningProvider>
          <Routes />
        </RouterWarningProvider>
      </ErrorBoundary>
    </Router>
  );
};
